import {DateTime} from 'luxon';
import {FilterSchema} from '@/hooks/usePathFilter';
import {ReservationListSearchFilterT} from '@/types/reservation/reservation-history-types';
import {
    SearchDateTypeE,
    KeywordTypeE,
    StayTypeE,
    ReservationStatusE,
    PolicyComplianceE,
} from '@/types/reservation/reservation-keyword-types';
import {serializeFilterToQuery, createDefaultFilter} from '@/utils/filterUtils';
import {SelectOption} from '@/types/util-types';

/**
 * 예약 내역 필터 기본값 계산
 */
const getDefaultDateValues = () => {
    const now = DateTime.now();
    return {
        fromDate: now.minus({days: 30}).toFormat('yyyy-MM-dd'),
        toDate: now.toFormat('yyyy-MM-dd'),
    };
};

/**
 * 날짜 타입 옵션 리스트
 */
export const DATE_TYPE_OPTION_LIST: SelectOption<SearchDateTypeE>[] = [
    {label: '결제일', value: SearchDateTypeE.PAYMENT},
    {label: '입실일', value: SearchDateTypeE.CHECKIN},
    {label: '퇴실일', value: SearchDateTypeE.CHECKOUT},
];

/*
 * 검색어 타입 옵션 (hasDivider 확장)
 */
type KeywordTypeOption = SelectOption<KeywordTypeE> & {
    hasDivider?: boolean;
};

/**
 * 검색어 타입 옵션 리스트
 * NOTE: 피그마 기준 순서로 정렬됨
 */
export const KEYWORD_TYPE_OPTION_LIST: KeywordTypeOption[] = [
    {label: '회원명', value: KeywordTypeE.USER_NAME},
    {label: '이메일 주소', value: KeywordTypeE.EMAIL, hasDivider: true},
    {label: '예약자명', value: KeywordTypeE.RESERVATION_NAME, hasDivider: true},
    {
        label: '예약자 휴대폰 번호',
        value: KeywordTypeE.RESERVATION_PHONE_NUMBER,
        hasDivider: true,
    },
    {
        label: '직급 또는 직위',
        value: KeywordTypeE.JOB_POSITION_NAME,
        hasDivider: true,
    },
    {label: '제휴점명', value: KeywordTypeE.FACILITY_NAME, hasDivider: true},
    {label: '그룹', value: KeywordTypeE.DEPARTMENT_ID, hasDivider: true},
    {label: '예약번호', value: KeywordTypeE.ORDER_ID},
];

/**
 * 권역 타입 옵션 리스트
 */
export const STAY_TYPE_OPTION_LIST: SelectOption<'ALL' | StayTypeE>[] = [
    {label: '권역 전체', value: 'ALL', formatter: () => '권역'},
    {label: '국내', value: StayTypeE.INSTAY},
    {label: '해외', value: StayTypeE.EXSTAY},
];

/**
 * 출장 상태 옵션 리스트
 */
export const RESERVATION_STATUS_OPTION_LIST: SelectOption<
    'ALL' | ReservationStatusE
>[] = [
    {label: '출장 상태 전체', value: 'ALL', formatter: () => '출장 상태'},
    {label: '예약완료', value: ReservationStatusE.CONFIRM},
    {label: '취소완료', value: ReservationStatusE.CANCEL},
    {label: '이용완료', value: ReservationStatusE.COMPLETE},
];

/**
 * 규정 준수 여부 옵션 리스트
 */
export const POLICY_COMPLIANCE_OPTION_LIST: SelectOption<
    'ALL' | PolicyComplianceE
>[] = [
    {
        label: '규정 준수 여부 전체',
        value: 'ALL',
        formatter: () => '규정 준수 여부',
    },
    {label: '준수', value: PolicyComplianceE.COMPLIANT},
    {label: '미준수', value: PolicyComplianceE.NON_COMPLIANT},
    {label: '해당없음', value: PolicyComplianceE.UNSPECIFIED},
];

/**
 * 기본 날짜 값 가져오기 (UI용)
 */
export const getDefaultDateRange = () => {
    const now = DateTime.now();
    return [
        now.minus({days: 30}).toFormat('yyyy-MM-dd'),
        now.toFormat('yyyy-MM-dd'),
    ] as [string, string];
};

/**
 * 예약 내역 필터 스키마
 */
export const reservationHistoryFilterSchema: FilterSchema<ReservationListSearchFilterT> =
    {
        dateType: {
            defaultValue: SearchDateTypeE.PAYMENT,
        },
        fromDate: {
            defaultValue: getDefaultDateValues().fromDate,
        },
        toDate: {
            defaultValue: getDefaultDateValues().toDate,
        },
        keywordType: {
            defaultValue: KeywordTypeE.USER_NAME,
        },
        keyword: {
            defaultValue: '',
            serialize: (value: string) => {
                return value === '' ? null : value;
            },
        },
        stayType: {
            defaultValue: 'ALL',
            serialize: (value) => {
                return value === 'ALL' ? null : (value as string);
            },
        },
        status: {
            defaultValue: 'ALL',
            serialize: (value) => {
                return value === 'ALL' ? null : (value as string);
            },
        },
        businessTripPolicyCompliantStatus: {
            defaultValue: 'ALL',
            serialize: (value) => {
                return value === 'ALL' ? null : (value as string);
            },
        },
        page: {
            defaultValue: 1,
            parse: (value) => parseInt(value ?? '1'),
        },
        size: {
            defaultValue: 10,
            parse: (value) => parseInt(value ?? '10'),
        },
    };

/**
 * 예약 내역 필터 검증 함수
 */
export const validateReservationHistoryFilter = (
    filter: ReservationListSearchFilterT,
): ReservationListSearchFilterT => {
    const nextFilter = {...filter};

    // keyword가 빈 문자열이면 null 처리
    if (nextFilter.keyword === '') {
        nextFilter.keyword = '';
    }

    return nextFilter;
};

export const getDefaultQueryParams = () =>
    serializeFilterToQuery(
        createDefaultFilter(
            reservationHistoryFilterSchema,
            validateReservationHistoryFilter,
        ),
        reservationHistoryFilterSchema,
    );
